/**
 * Predykcja ruchu lokalnego gracza — ten sam stepMovement co serwer (§6.2).
 * Korekta gdy |server − local| > CORRECTION_THRESHOLD_PX (32).
 */
import {
  stepMovement,
  CORRECTION_THRESHOLD_PX,
  DOG_SPEED,
  type Bounds,
  type MovementState,
} from '@pieskowo/shared';

export type ServerPose = {
  x: number;
  y: number;
  tx: number;
  ty: number;
};

/** Jeden krok predykcji (dt w sekundach). */
export function predictStep(
  state: MovementState,
  dtSec: number,
  bounds?: Bounds,
  speed: number = DOG_SPEED,
): MovementState {
  const dt = Math.max(0, Math.min(0.1, dtSec));
  return stepMovement(state, dt, speed, bounds);
}

/**
 * Korekta względem autorytetu serwera.
 * - dystans > threshold → snap do serwera (pozycja + cel)
 * - inaczej → zachowaj lokalną pozycję, przejmij cel serwera
 */
export function applyServerCorrection(
  local: MovementState,
  server: ServerPose,
  thresholdPx: number = CORRECTION_THRESHOLD_PX,
): MovementState {
  const dist = Math.hypot(local.x - server.x, local.y - server.y);
  if (dist > thresholdPx) {
    return {
      x: server.x,
      y: server.y,
      tx: server.tx,
      ty: server.ty,
    };
  }
  return {
    x: local.x,
    y: local.y,
    tx: server.tx,
    ty: server.ty,
  };
}

/** Ustaw cel ruchu (tap-to-move). */
export function setMoveTarget(
  state: MovementState,
  x: number,
  y: number,
  bounds?: Bounds,
): MovementState {
  let tx = x;
  let ty = y;
  if (bounds) {
    tx = Math.min(bounds.maxX, Math.max(bounds.minX, tx));
    ty = Math.min(bounds.maxY, Math.max(bounds.minY, ty));
  }
  return { ...state, tx, ty };
}

export { CORRECTION_THRESHOLD_PX, DOG_SPEED };
